import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Blog.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://sbg-backend.onrender.com';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        // Fetch published blog posts from the backend API
        const response = await axios.get(`${API_URL}/api/blog?isPublished=true`);
        setPosts(response.data.posts || []);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setError('Unable to load blog posts. Please try again later.');
        
        // Provide mock data when API call fails
        const mockPosts = [
          {
            _id: 'mock-1',
            title: 'Top Recruiting Strategies for 2024',
            content: `<h2>Introduction</h2>
            <p>Recruiting top talent has become increasingly competitive in 2024. Companies need to adapt their strategies to attract the <strong>best candidates</strong> in a changing job market. With unemployment rates at historic lows, organizations must be creative and proactive in their approach.</p>
            
            <h2>Key Strategies</h2>
            <p>Successful recruiting in today's market requires a multi-faceted approach that combines technology with human connection. Here are some proven strategies that can help your organization stand out:</p>
            
            <h3>Leverage AI-Powered Recruiting Tools</h3>
            <p>Artificial intelligence can help streamline your recruiting process by automatically screening resumes and identifying top candidates. These tools can:</p>
            <ul>
              <li><strong>Save time</strong> by automating repetitive tasks</li>
              <li>Reduce bias in the screening process</li>
              <li>Identify candidates who might be overlooked by traditional methods</li>
              <li>Provide data-driven insights to improve your recruiting strategy</li>
            </ul>
            
            <h3>Focus on Employer Branding</h3>
            <p>Your company's reputation as an employer is crucial for attracting quality candidates. Invest in building a positive employer brand by:</p>
            <ul>
              <li>Showcasing your company culture on social media</li>
              <li>Encouraging employees to share their experiences</li>
              <li>Highlighting benefits and growth opportunities</li>
              <li><strong>Responding professionally</strong> to reviews on job sites</li>
            </ul>
            
            <h2>Conclusion</h2>
            <p>By implementing these strategies, you can improve your recruiting outcomes and build a stronger team for your organization. Remember that the most successful recruiting approaches combine technological innovation with a human touch.</p>
            
            <p>Partner with a staffing agency like <strong>Scott Business Group</strong> to leverage industry expertise and expand your reach to passive candidates who might be the perfect fit for your organization.</p>`,
            excerpt: 'Discover the most effective recruiting strategies for finding top talent in 2024.',
            author: 'Scott Business Group',
            createdAt: new Date().toISOString(),
            publishedAt: new Date().toISOString(),
            isPublished: true,
            tags: ['recruiting', 'hiring', 'strategies']
          },
          {
            _id: 'mock-2',
            title: 'How to Prepare for a Technical Interview',
            content: `<h2>Introduction</h2>
            <p>Technical interviews can be intimidating, but with the right preparation, you can showcase your skills and land your dream job. Whether you're interviewing for a position in software development, data science, or engineering, these tips will help you approach your interview with <strong>confidence and competence</strong>.</p>
            
            <h2>Preparation Tips</h2>
            <p>Successful technical interviews require both technical knowledge and soft skills. Here's how to prepare effectively:</p>
            
            <h3>Review Fundamentals</h3>
            <p>Make sure you have a solid understanding of the core concepts in your field. Review data structures, algorithms, and common problem-solving techniques. Focus on:</p>
            <ul>
              <li><strong>Data structures</strong> (arrays, linked lists, trees, graphs, hash tables)</li>
              <li><strong>Algorithms</strong> (sorting, searching, dynamic programming)</li>
              <li>Time and space complexity analysis</li>
              <li>System design principles for senior roles</li>
            </ul>
            
            <h3>Practice Mock Interviews</h3>
            <p>Conduct practice interviews with friends or use online platforms that simulate technical interviews. This will help you:</p>
            <ol>
              <li>Get comfortable explaining your thought process out loud</li>
              <li>Improve your problem-solving speed</li>
              <li>Receive feedback on areas for improvement</li>
              <li>Reduce interview anxiety through familiarity</li>
            </ol>
            
            <h3>Research the Company</h3>
            <p>Understanding the company's products, technologies, and culture can give you an edge in the interview. Be prepared to discuss:</p>
            <ul>
              <li>How your skills align with their tech stack</li>
              <li>Relevant projects you've worked on</li>
              <li>How you can contribute to their specific challenges</li>
            </ul>
            
            <h2>Conclusion</h2>
            <p>With thorough preparation and practice, you can approach your technical interview with confidence and increase your chances of success. Remember to <strong>stay calm</strong>, communicate clearly, and demonstrate not just your technical abilities but also your problem-solving approach.</p>
            
            <p>Working with a specialized recruiter from <strong>Scott Business Group</strong> can help you prepare specifically for the companies and roles you're targeting, giving you valuable insights into what employers are looking for.</p>`,
            excerpt: 'Learn how to ace your next technical interview with these proven preparation tips.',
            author: 'Scott Business Group',
            createdAt: new Date(Date.now() - 86400000).toISOString(),
            publishedAt: new Date(Date.now() - 86400000).toISOString(),
            isPublished: true,
            tags: ['interview', 'technical', 'preparation']
          }
        ];
        
        setPosts(mockPosts);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-green-50 min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-green-800 mb-8 border-b-4 border-orange-500 pb-4 inline-block">Blog</h1>
        
        {error && (
          <div className="bg-yellow-50 border-l-4 border-orange-500 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-orange-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-orange-700">{error}</p>
              </div>
            </div>
          </div>
        )}
        
        {posts.length === 0 ? (
          <p className="text-gray-600">No blog posts available yet.</p>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((post) => (
              <Link 
                to={`/blog/${post._id}`} 
                key={post._id}
                className="block hover:no-underline"
              >
                <article
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 h-full blog-card border-l-4 border-orange-500"
                >
                  {post.coverImage && (
                    <div className="relative">
                      <img
                        src={post.coverImage}
                        alt={post.title}
                        className="w-full h-48 object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-green-900/40 to-transparent"></div>
                    </div>
                  )}
                  <div className="p-6">
                    <h2 className="text-xl font-semibold text-green-800 mb-2 hover:text-orange-600 transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.excerpt || post.content.substring(0, 150) + '...'}
                    </p>
                    <div className="flex items-center justify-between text-sm text-green-700 border-t border-green-100 pt-4">
                      <span className="font-medium">{post.author}</span>
                      <span className="bg-orange-100 px-2 py-1 rounded-full text-orange-800">
                        {post.publishedAt && new Date(post.publishedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;